<template>
  <div v-loading.fullscreen="isMOLoading" class="flex flex-col responsive-page fixed-table">
    <div class="header-container-wrapper">
      <div class="flex items-center pb-8 border-solid border-b-1 border-gray-300 header-container">
        <div class="flex-grow items-center flex header-container__left">
          <PageHeader :title="$t('mo_list')" @load:refreshList="fetchList" />
        </div>
        <div class="flex header-container__right">
          <el-input
            class="w-60"
            prefix-icon="el-icon-search"
            v-model="dataSearch.orderNumber"
            @keyup="refreshItemList"
            :placeholder="$t('search_mo_placeholder')"
          />
        </div>
      </div>
    </div>

    <div v-if="getMOItems.length > 0">
      <div class="full-page-width flex justify-between table-actions py-5">
        <span class="font-bold text-xl">{{ $t('mo_list') }}</span>
        <el-button @click="exportCSV" class="excel-export-btn">{{ $t('barcode.excel_export') }}</el-button>
      </div>
      <div class="mb-16 flex-grow">
        <MoList ref="moListRef" :moData="getMOItems" @detail="handleDetail" @sort-change="handleSortChange" />
      </div>
      <div class="justify-center flex flex-row mb-16 paging-container">
        <el-pagination
          layout="prev, pager, next"
          :total="getTotalRecords"
          :page-size="itemsPerPage"
          :current-page="currentPageNum"
          @current-change="currentPageChange"
          background
          class="custom-pagination"
        />
      </div>
    </div>

    <template v-else>
      <ItemNotFound class="mt-56" :content="$t('empty')" />
    </template>
  </div>
</template>

<script lang="ts">
import ItemNotFound from '@/components/common/ItemNotFound.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import MoList from '@/components/mo/MoList.vue'
import DataTableMixin from '@/components/mixins/DataTableMixin.vue'
import { EXPORT_MANUFACTURING_ORDER, FETCH_MANUFACTURING_ORDERS, SET_HAVE_NEW_EXPORT_RECORD } from '@/store/actions'
import { FILTER_DEBOUNCE_TIME } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { IManufacturingPlan } from '@/utils/types/model/manufacturingPlan'
import debounce from 'lodash/debounce'
import { Options } from 'vue-class-component'
import { Mixins, Watch } from 'vue-property-decorator'
import { EExportType } from '@/utils/enums'

interface ICommonSearch {
  orderNumber?: string
  sortBy?: string
  sortType?: string
  [key: string]: string | undefined
}

@Options({
  components: { PageHeader, ItemNotFound, MoList },
  name: 'ManufacturingPlan',
})
export default class ManufacturingPlan extends Mixins(DataTableMixin) {
  dataSearch: ICommonSearch = {}
  debounceFilter!: () => void

  get isMOLoading(): boolean {
    return this.$store.getters.isMOLoading || false
  }

  get getTotalRecords(): number {
    return this.$store.getters.getTotalRecords || 0
  }

  get getMOItems(): IManufacturingPlan[] {
    return this.$store.getters.getMOItems || []
  }

  refreshItemList() {
    this.debounceFilter()
  }

  handleDetail(orderId: number | string) {
    this.$router.push({ name: 'mo-detail', params: { id: orderId.toString() } })
  }

  handleSortChange(data: { prop: string; order: string | null }) {
    this.dataSearch.sortBy = data.prop
    this.dataSearch.sortType = data.order ? (data.order === 'ascending' ? 'asc' : 'desc') : undefined
    this.currentPageNum = 1
    this.redirectIndex()
    this.fetchList()
  }

  async exportCSV() {
    try {
      await this.$store.dispatch(EXPORT_MANUFACTURING_ORDER, {
        sortBy: this.dataSearch.sortBy,
        sortType: this.dataSearch.sortType,
      })
      this.$store.commit(SET_HAVE_NEW_EXPORT_RECORD, { exportType: EExportType.MASTER_DATA, isHaveNew: true })
    } catch (e) {
      errorHandler(e)
    }
  }

  fetchList() {
    try {
      this.$store.dispatch(FETCH_MANUFACTURING_ORDERS, {
        page: this.currentPageNum,
        limit: this.itemsPerPage,
        orderNumber: this.dataSearch.orderNumber || '',
        sortBy: this.dataSearch.sortBy,
        sortType: this.dataSearch.sortType,
      })
    } catch (e) {
      errorHandler(e, {}, true)
    }
  }

  created() {
    this.routeName = 'manufacturing-plan'
    this.getQueryData()
    this.debounceFilter = debounce(() => {
      this.currentPageNum = 1
      this.redirectIndex()
      this.fetchList()
    }, FILTER_DEBOUNCE_TIME)
    this.fetchList()
  }

  currentPageChange(pageNum: number) {
    this.currentPageNum = pageNum
    this.redirectIndex()
    this.fetchList()
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';
.header-container-wrapper {
  background-color: $body-bg;
}

.fixed-table:deep() {
  height: calc(100vh - 100px);
  min-width: 800px;

  &.table-content {
    overflow: auto;
    height: calc(100vh - 350px);
    min-height: 200px;
  }
  &.el-table {
    &::before {
      height: 0px;
    }
    &__header-wrapper {
      position: sticky;
      position: -webkit-sticky;
      top: 0;
      z-index: 10;
    }
  }
}
</style>
